import { useEffect, type RefObject } from "react";

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

function focusableInside(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (element) => element.offsetParent !== null || element === document.activeElement,
  );
}

export interface FocusTrapOptions {
  /** Se llama con Escape. Si no se pasa, Escape no hace nada. */
  onEscape?: () => void;
  /**
   * Devuelve el foco al elemento que lo tenía antes de abrir. Por defecto
   * sí: quien abrió el cajón con el teclado tiene que volver a su botón,
   * no al principio del documento.
   */
  restoreFocus?: boolean;
}

/**
 * Atrapa el foco dentro de `containerRef` mientras `active` sea cierto.
 *
 * Qué hace, y por qué cada parte:
 *   · Al activarse, mueve el foco al primer elemento enfocable de dentro.
 *     Sin esto el lector de pantalla sigue leyendo la página de debajo y
 *     el usuario de teclado no tiene forma de llegar al panel que acaba
 *     de abrir.
 *   · Tab y Shift+Tab dan la vuelta en vez de salirse. Un cajón con
 *     `aria-modal` que deja escapar el foco miente: el lector anuncia que
 *     lo de fuera está inerte y el teclado demuestra que no.
 *   · Escape cierra (si se pasa `onEscape`).
 *   · Al desactivarse devuelve el foco a donde estaba.
 *
 * En jsdom `offsetParent` es siempre null, así que el filtro de
 * visibilidad se relaja para no dejar la lista vacía en pruebas — de ahí
 * el `|| element === document.activeElement` y el fallback de abajo.
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  active: boolean,
  { onEscape, restoreFocus = true }: FocusTrapOptions = {},
): void {
  useEffect(() => {
    if (!active) return;

    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    const list = () => {
      const visible = focusableInside(container);
      if (visible.length > 0) return visible;
      // jsdom no calcula layout: `offsetParent` es null para todo.
      return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));
    };

    list()[0]?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onEscape?.();
        return;
      }
      if (event.key !== "Tab") return;

      const items = list();
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (restoreFocus) previouslyFocused?.focus?.();
    };
  }, [active, containerRef, onEscape, restoreFocus]);
}
